import {Controller, useFieldArray, useFormContext} from 'react-hook-form';
import {AnnouncementFormValues} from '../types';
import {SVG} from '../../../../../../constants/assetLists';
import AppSwitch from '../../../../../../components/common/AppSwitch';
import AppButton from '../../../../../../components/common/AppButton';
import Recipients from './Recipients';
import Files from './Files';
import Equipment from './Equipment';
import Checklist from './Checklist';
import {useEffect, useRef, useState} from 'react';

function TaskDetailSection({
  idx,
  isExpanded,
  handleExpandSection,
  expandedSections,
}: {
  idx: number;
  isExpanded: boolean;
  handleExpandSection: (section: string) => void;
  expandedSections: string[];
}) {
  const sectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isExpanded) return;
    requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView({behavior: 'smooth', block: 'end'});
    });
  }, [isExpanded]);

  const renderTaskDetailSection = (icon: string, title: string) => {
    return (
      <div className="flex items-center gap-4">
        <div className="w-[24px] h-[24px]">
          <img
            src={icon}
            alt="icon"
            className="w-[20px] h-[20px] object-contain"
          />
        </div>
        <p className="m-0 text-[20px] font-medium text-black">{title}</p>
      </div>
    );
  };

  return (
    <div ref={sectionRef} className="px-2 flex flex-col gap-3">
      {renderTaskDetailSection(SVG.clockIcon, 'בטווח זמני ההודעה')}
      <Recipients
        forForm="task"
        index={idx}
        handleExpandSection={handleExpandSection}
        expandedSections={expandedSections}
      />
      <Files
        forForm="task"
        index={idx}
        handleExpandSection={handleExpandSection}
        expandedSections={expandedSections}
      />
      <Equipment
        forForm="task"
        index={idx}
        handleExpandSection={handleExpandSection}
        expandedSections={expandedSections}
      />
      <Checklist
        forForm="task"
        index={idx}
        handleExpandSection={handleExpandSection}
        expandedSections={expandedSections}
      />
    </div>
  );
}

function Task({
  handleExpandSection,
  expandedSections,
  setExpandedSections,
}: {
  handleExpandSection: (section: string) => void;
  expandedSections: string[];
  setExpandedSections: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const [showTaskSection, setShowTaskSection] = useState(false);

  const bottomRef = useRef<HTMLDivElement | null>(null);

  const {
    watch,
    control,
    setValue,
    formState: {errors},
  } = useFormContext<AnnouncementFormValues>();

  const {
    fields: taskFields,
    append: taskAppend,
    remove: taskRemove,
  } = useFieldArray<AnnouncementFormValues, 'tasks', 'id'>({
    control: control,
    name: 'tasks',
  });

  const tasks = watch('tasks');
  // const tasksLength = tasks?.filter(task => task.taskTitle)?.length || 0;
  const tasksLength = tasks?.length || 0;

  useEffect(() => {
    if (!showTaskSection) return;
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({behavior: 'smooth', block: 'end'});
    });
  }, [showTaskSection, taskFields.length]);

  const handleAddTask = () => {
    taskAppend({
      taskTitle: '',
      needApproval: false,
      recipients: [],
      files: [],
      equipments: [{value: ''}],
      checklists: [],
    });

    if (!showTaskSection) setShowTaskSection(true);
  };

  const handleRemoveTask = (index: number) => {
    // Special handling for removing the last task
    const currentTasks = watch('tasks');
    const isLastTask = currentTasks.length === 1;

    if (isLastTask) {
      setExpandedSections(prev =>
        prev.filter(
          section =>
            section !== 'task' &&
            !section.includes('taskDetails-') &&
            !section.includes('task-recipients-') &&
            !section.includes('task-files-') &&
            !section.includes('task-equipment-') &&
            !section.includes('task-checklist-'),
        ),
      );

      // Small delay to ensure state is updated before removing the task
      setTimeout(() => {
        taskRemove(index);
      }, 0);
    } else {
      // For non-last tasks, remove normally
      taskRemove(index);
    }
  };

  const handleTaskSectionExpand = () => {
    if (taskFields?.length > 0) {
      setShowTaskSection(!showTaskSection);
    } else {
      handleAddTask();
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-start">
        <div
          className="flex items-center gap-4 cursor-pointer hover:opacity-80 transition-opacity duration-200"
          onClick={handleTaskSectionExpand}>
          <div className="w-[20px] h-[19px]">
            <img
              src={SVG.taskIcon}
              alt="icon"
              className="w-full h-full object-contain"
            />
          </div>
          <p className="m-0 text-[20px] font-medium text-black">
            הוספת משימות {tasksLength > 2 ? '+2' : tasksLength || ''}
          </p>
        </div>
      </div>

      {showTaskSection && (
        <>
          <div className="flex flex-col gap-3">
            {taskFields.map((task, idx) => {
              const isExpandedTaskDetail = expandedSections.includes(
                `taskDetails-${idx}`,
              );

              return (
                <div
                  key={task.id}
                  ref={bottomRef}
                  className="p-3 bg-grayBg rounded-2xl flex flex-col gap-3"
                >
                  <div className="flex items-center gap-3 overflow-hidden">
                    <div className="w-[20px] h-[19px]">
                      <img
                        src={SVG.taskIcon}
                        alt="icon"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <Controller
                      control={control}
                      name={`tasks.${idx}.taskTitle`}
                      render={({ field }) => {
                        return (
                          <div className="flex-1 w-full ">
                            <div className="flex items-center gap-1 border-b-[3px] border-b-linkBlue">
                              {!field.value && (
                                <span className="m-0 text-[32px] font-medium text-raspberryRed">
                                  *
                                </span>
                              )}

                              <input
                                className="w-full m-0 text-[32px] text-black bg-transparent outline-none placeholder:text-black"
                                placeholder={"כותרת"}
                                autoComplete="off"
                                value={field.value}
                                onChange={(e) =>
                                  setValue(
                                    `tasks.${idx}.taskTitle`,
                                    e.target.value,
                                    {
                                      shouldValidate: true,
                                      shouldDirty: true,
                                      shouldTouch: true,
                                    },
                                  )
                                }
                              />
                            </div>

                            {errors?.tasks?.length && (
                              <p className="m-0 text-[12px] text-raspberryRed">
                                {errors?.tasks[idx!]?.taskTitle?.message}
                              </p>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <Controller
                      control={control}
                      name={`tasks.${idx}.needApproval`}
                      render={({ field }) => {
                        return (
                          <AppSwitch
                            {...field}
                            checked={field.value}
                            outlined
                          />
                        );
                      }}
                    />
                    <p
                      className={`m-0 text-[20px] text-black ${watch(`tasks.${idx}.needApproval`) ? "font-medium" : ""}`}
                    >
                      הפעלת חובת אישור
                    </p>
                  </div>

                  <div
                    className={`p-2 rounded-[3px] flex flex-col gap-3 ${
                      isExpandedTaskDetail && "bg-white"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <button
                        className="w-[12px] h-[12px] hover:opacity-40 transition-opacity duration-200"
                        onClick={() =>
                          handleExpandSection(`taskDetails-${idx}`)
                        }
                      >
                        <img
                          src={
                            isExpandedTaskDetail
                              ? SVG.expandIcon
                              : SVG.lessThanIcon
                          }
                          alt="icon"
                          className="w-full h-full object-contain"
                        />
                      </button>
                      <p className="m-0 text-[20px] font-medium text-black">
                        אפשרויות נוספות
                      </p>
                    </div>

                    {isExpandedTaskDetail && (
                      <TaskDetailSection
                        idx={idx}
                        isExpanded={isExpandedTaskDetail}
                        handleExpandSection={handleExpandSection}
                        expandedSections={expandedSections}
                      />
                    )}
                  </div>

                  <div className="flex justify-end">
                    <AppButton
                      variant="secondary"
                      iconStart={
                        <img
                          src={SVG.binBlackIcon}
                          className="w-[12px] h-[13.5px]"
                          alt={"icon"}
                        />
                      }
                      onClick={() => handleRemoveTask(idx)}
                    >
                      מחיקה
                    </AppButton>
                  </div>
                </div>
              );
            })}
          </div>

          {taskFields.length > 0 && (
            <div>
              <AppButton
                iconStart={
                  <div className="w-[12px] h-[12px]">
                    <img
                      src={SVG.addIcon}
                      alt="icon"
                      className="w-full h-full object-contain"
                    />
                  </div>
                }
                onClick={handleAddTask}>
                הוספת משימה
              </AppButton>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Task;
